import React from 'react';
import { CATEGORIES } from '../../lib/supplierDashboardConstants';
import { cityInEnglish } from '../../lib/cityNames';

// The supplier mini-card. THREE LINES, and nothing else, wherever a trader is
// shown who quoted them.
//
//   1. Company name + city
//   2. "✓ Business licence verified by Maabar"   — ONLY when approved
//   3. "Manufacturer — {category}"
//
// What is deliberately absent, and must stay absent (owner, 2026-09-19):
//   · No Alibaba/1688 or any trade link. That is the trader's
//     price-comparison and bypass channel in one click; it lives admin-side.
//   · No join date. Young platform — tenure is a liability until it isn't.
//   · No phone, WhatsApp, WeChat or email. Ever.
//   · Empty field → HIDDEN ROW. Never "N/A", never an empty line.
//
// THE UNVERIFIED CASE. A supplier at pending / registered /
// verification_required / verification_under_review renders the card with the
// tick line simply ABSENT. Never "not verified", never "unverified", no
// negative phrasing and no grey cross. We do not brand a supplier to the
// trader as failing a check; we only decline to claim something we have not
// checked ourselves. A rejected supplier produces no card at all — the RPC
// returns no row.

const T = {
  ar: { verified: '✓ الرخصة التجارية موثّقة من مَعبر', manufacturer: 'مصنّع' },
  en: { verified: '✓ Business licence verified by Maabar', manufacturer: 'Manufacturer' },
  zh: { verified: '✓ 营业执照已由 Maabar 核验', manufacturer: '制造商' },
};

export default function SupplierMiniCard({ card, lang = 'ar', style }) {
  if (!card || !card.company_name) return null;

  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? { fontFamily: 'var(--font-ar)' } : {};

  // City is mapped out of Arabic for non-Arabic readers and DROPPED when it
  // cannot be mapped — the same rule the public RFQ page uses, for the same
  // reason: a line of script the reader cannot parse is worse than no line.
  const city = isAr ? (card.city || '') : cityInEnglish(card.city);
  const line1 = [card.company_name, city].filter(Boolean).join(' — ');

  const catList = CATEGORIES[lang] || CATEGORIES.en || CATEGORIES.ar || [];
  const catLabel = card.speciality
    ? (catList.find((c) => c.val === card.speciality)?.label || card.speciality)
    : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, ...font, ...style }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.45 }}>
        {line1}
      </span>

      {/* Line 2 — only when we have actually checked. Absent otherwise. */}
      {card.verified && (
        <span style={{ fontSize: 12.5, color: 'var(--green, #2D6A4F)', lineHeight: 1.5 }}>
          {t.verified}
        </span>
      )}

      {/* Line 3 — hidden entirely when the supplier has no category. */}
      {catLabel && (
        <span style={{ fontSize: 12.5, color: 'var(--text-disabled)', lineHeight: 1.5 }}>
          {t.manufacturer} — {catLabel}
        </span>
      )}
    </div>
  );
}
